import React, { useState, useEffect, useCallback } from "react";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  FiArrowLeft,
  FiChevronRight,
  FiSearch,
  FiRefreshCw,
  FiExternalLink,
  FiX,
  FiCopy,
  FiCheck,
  FiEye,
  FiUser,
} from "react-icons/fi";
import { getEventPointsLedger } from "../api/eventPointsApi";
import { getEvents } from "../api/eventApi";

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    return d.toLocaleString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  } catch {
    return String(dateStr);
  }
};

const GAME_TABS = [
  { key: "all", label: "All Games", icon: "🎮", color: "gray" },
  { key: "game_2048", label: "2048 Game", icon: "🧩", color: "amber" },
  { key: "fruit_Bomb", label: "Fruit Bomb", icon: "🍉", color: "rose" },
  { key: "tap", label: "Tap & Earn", icon: "⚡", color: "yellow" },
  { key: "boomcards", label: "Boom Cards", icon: "🃏", color: "blue" },
  { key: "slots", label: "777 Slots", icon: "🎰", color: "purple" },
  { key: "spinwheel", label: "Spin Wheel", icon: "🎡", color: "emerald" },
];

const getGameBadge = (gameKey) => {
  const k = String(gameKey || "").toLowerCase();
  if (k.includes("2048")) {
    return { label: "2048 Game", icon: "🧩", style: "bg-amber-100 text-amber-800 border-amber-300" };
  }
  if (k.includes("fruit")) {
    return { label: "Fruit Bomb", icon: "🍉", style: "bg-rose-100 text-rose-800 border-rose-300" };
  }
  if (k.includes("tap")) {
    return { label: "Tap & Earn", icon: "⚡", style: "bg-yellow-100 text-yellow-800 border-yellow-300" };
  }
  if (k.includes("boom")) {
    return { label: "Boom Cards", icon: "🃏", style: "bg-blue-100 text-blue-800 border-blue-300" };
  }
  if (k.includes("slot")) {
    return { label: "777 Slots", icon: "🎰", style: "bg-purple-100 text-purple-800 border-purple-300" };
  }
  if (k.includes("spin") || k.includes("wheel")) {
    return { label: "Spin Wheel", icon: "🎡", style: "bg-emerald-100 text-emerald-800 border-emerald-300" };
  }
  return { label: gameKey || "Mini Game", icon: "🎮", style: "bg-gray-100 text-gray-800 border-gray-300" };
};

export default function EventPointsLedger() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const handleGoBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate("/events");
    }
  };

  // State
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [selectedGameKey, setSelectedGameKey] = useState(
    searchParams.get("gameKey") || "all"
  );
  const [userIdFilter, setUserIdFilter] = useState(
    searchParams.get("userId") || ""
  );
  const [search, setSearch] = useState("");
  const [selectedEventId, setSelectedEventId] = useState("all");
  const [eventsList, setEventsList] = useState([]);

  // Pagination
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 1,
    page: 1,
    perPage: 20,
  });

  // Per-game Summary Cards
  const [totalEventPoints, setTotalEventPoints] = useState(0);
  const [gamesSummary, setGamesSummary] = useState({
    game_2048: { points: 0, sessions: 0 },
    fruit_Bomb: { points: 0, sessions: 0 },
    tap: { points: 0, sessions: 0 },
    boomcards: { points: 0, sessions: 0 },
    slots: { points: 0, sessions: 0 },
    spinwheel: { points: 0, sessions: 0 },
  });

  // UI state
  const [selectedSession, setSelectedSession] = useState(null);
  const [copiedId, setCopiedId] = useState("");

  // Load Events on Mount
  useEffect(() => {
    async function initEvents() {
      try {
        const res = await getEvents({ limit: 100, page: 1, status: "all" });
        const list = res?.data?.events || (Array.isArray(res?.data) ? res.data : []);
        setEventsList(list);
      } catch (e) {
        console.warn("Could not load events:", e.message);
      }
    }
    initEvents();
  }, []);

  // Fetch Event Points Ledger
  const fetchLedger = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getEventPointsLedger({
        userId: userIdFilter,
        gameKey: selectedGameKey,
        eventId: selectedEventId !== "all" ? selectedEventId : undefined,
        page,
        perPage,
        search,
      });

      if (res?.status && res.data) {
        const d = res.data;
        const list = Array.isArray(d)
          ? d
          : Array.isArray(d.history)
          ? d.history
          : Array.isArray(d.rows)
          ? d.rows
          : [];

        setHistory(list);

        if (d.totalEventPoints != null) {
          setTotalEventPoints(Number(d.totalEventPoints));
        } else {
          // calculate fallback from rows
          const sum = list.reduce(
            (acc, r) => acc + (Number(r.eventPoints ?? r.pointsAwarded ?? r.points ?? 0) || 0),
            0
          );
          setTotalEventPoints(sum);
        }

        if (d.gamesSummary) {
          setGamesSummary((prev) => ({
            ...prev,
            ...d.gamesSummary,
          }));
        }

        const serverPagination = d.pagination || res.pagination || {};
        const totalRecords =
          typeof serverPagination.total === "number"
            ? serverPagination.total
            : typeof res.total === "number"
            ? res.total
            : list.length;

        const totalPages =
          typeof serverPagination.totalPages === "number"
            ? serverPagination.totalPages
            : Math.max(1, Math.ceil(totalRecords / perPage));

        setPagination({
          total: totalRecords,
          totalPages,
          page,
          perPage,
        });
      }
    } catch (err) {
      console.warn("Error fetching Event Points Ledger:", err.message);
      toast.error(err.message || "Failed to load Event Points Ledger");
    } finally {
      setLoading(false);
    }
  }, [userIdFilter, selectedGameKey, selectedEventId, page, perPage, search]);

  useEffect(() => {
    fetchLedger();
  }, [fetchLedger]);

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(String(text));
    setCopiedId(id);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopiedId(""), 2000);
  };

  const handleGameSelect = (gKey) => {
    setSelectedGameKey(gKey);
    setPage(1);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (gKey === "all") next.delete("gameKey");
      else next.set("gameKey", gKey);
      return next;
    });
  };

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      <Sidebar />

      <div className="flex-1 flex flex-col min-w-0 overflow-y-auto">
        <Navbar />

        <div className="p-4 sm:p-6 lg:p-8 max-w-7xl w-full mx-auto space-y-6">
          {/* Breadcrumb Navigation & Back Button */}
          <div className="flex flex-wrap items-center justify-between gap-4 pb-2 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <button
                onClick={handleGoBack}
                className="p-2 rounded-xl bg-white border border-gray-200 text-gray-700 hover:bg-gray-100 hover:text-black transition shadow-2xs cursor-pointer"
                title="Go Back"
              >
                <FiArrowLeft className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <Link to="/dashboard" className="hover:text-gray-900 transition">
                  Dashboard
                </Link>
                <FiChevronRight className="w-3 h-3 text-gray-400" />
                <Link to="/events" className="hover:text-gray-900 transition">
                  Events
                </Link>
                <FiChevronRight className="w-3 h-3 text-gray-400" />
                <span className="font-semibold text-gray-800">Event Points Ledger</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Event Filter Dropdown */}
              {eventsList.length > 0 && (
                <select
                  value={selectedEventId}
                  onChange={(e) => {
                    setSelectedEventId(e.target.value);
                    setPage(1);
                  }}
                  className="bg-white border border-gray-200 text-xs text-gray-800 rounded-xl px-3 py-1.5 font-medium shadow-2xs focus:outline-none focus:border-yellow-400"
                >
                  <option value="all">All Events</option>
                  {eventsList.map((evt) => (
                    <option key={evt.eventId || evt.id} value={evt.eventId || evt.id}>
                      {evt.title || evt.name || `Event #${evt.eventId || evt.id}`}
                    </option>
                  ))}
                </select>
              )}

              <button
                onClick={fetchLedger}
                className="p-2 rounded-xl bg-white border border-gray-200 text-gray-600 hover:text-black hover:bg-gray-100 transition shadow-2xs cursor-pointer"
                title="Refresh"
              >
                <FiRefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              </button>
            </div>
          </div>

          {/* Page Hero Header */}
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-gray-900 via-gray-800 to-black text-white p-6 shadow-xl border border-gray-800">
            <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-2xl">🎯</span>
                  <span className="bg-[#ffff00] text-black text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded">
                    Global Ledger
                  </span>
                  <span className="bg-white/20 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded">
                    Audit Verification
                  </span>
                </div>
                <h1 className="text-2xl sm:text-3xl font-black tracking-tight">
                  Dedicated Event Points Ledger
                </h1>
                <p className="text-gray-300 text-xs sm:text-sm mt-1 max-w-2xl">
                  Unified audit ledger of all event points distributed across 2048, Fruit Bomb, Tap & Earn, Boom Cards, 777 Slots, and Spin Wheel.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-xs p-4 rounded-xl border border-white/10 text-right shrink-0">
                <div className="text-[10px] uppercase tracking-wider text-[#ffff00] font-bold">
                  Total Points Distributed
                </div>
                <div className="text-2xl font-black text-white font-mono mt-0.5">
                  {totalEventPoints.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} pts
                </div>
                <div className="text-[10px] text-gray-400">Across All Mini Games</div>
              </div>
            </div>
          </div>

          {/* Per-Game Summary Cards (Section 4.2 Specs) */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {/* 2048 Game */}
            <div
              onClick={() => handleGameSelect("game_2048")}
              className={`p-4 rounded-2xl border transition cursor-pointer ${
                selectedGameKey === "game_2048"
                  ? "bg-amber-500 text-white border-amber-600 shadow-md scale-102"
                  : "bg-white text-gray-900 border-gray-200/80 hover:border-amber-300 shadow-2xs"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="text-xl">🧩</span>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                  selectedGameKey === "game_2048" ? "bg-black/30 text-white" : "bg-amber-100 text-amber-800"
                }`}>
                  2048
                </span>
              </div>
              <div className="mt-3">
                <div className="text-[11px] font-bold opacity-80 uppercase">2048 Game</div>
                <div className="text-lg font-black font-mono mt-0.5">
                  {(gamesSummary.game_2048?.points || 0).toLocaleString()}
                </div>
                <div className="text-[10px] opacity-70">
                  {(gamesSummary.game_2048?.sessions || 0).toLocaleString()} sessions
                </div>
              </div>
            </div>

            {/* Fruit Bomb */}
            <div
              onClick={() => handleGameSelect("fruit_Bomb")}
              className={`p-4 rounded-2xl border transition cursor-pointer ${
                selectedGameKey === "fruit_Bomb"
                  ? "bg-rose-500 text-white border-rose-600 shadow-md scale-102"
                  : "bg-white text-gray-900 border-gray-200/80 hover:border-rose-300 shadow-2xs"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="text-xl">🍉</span>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                  selectedGameKey === "fruit_Bomb" ? "bg-black/30 text-white" : "bg-rose-100 text-rose-800"
                }`}>
                  Fruit Bomb
                </span>
              </div>
              <div className="mt-3">
                <div className="text-[11px] font-bold opacity-80 uppercase">Fruit Bomb</div>
                <div className="text-lg font-black font-mono mt-0.5">
                  {(gamesSummary.fruit_Bomb?.points || 0).toLocaleString()}
                </div>
                <div className="text-[10px] opacity-70">
                  {(gamesSummary.fruit_Bomb?.sessions || 0).toLocaleString()} sessions
                </div>
              </div>
            </div>

            {/* Tap & Earn */}
            <div
              onClick={() => handleGameSelect("tap")}
              className={`p-4 rounded-2xl border transition cursor-pointer ${
                selectedGameKey === "tap"
                  ? "bg-yellow-500 text-black border-yellow-600 shadow-md scale-102"
                  : "bg-white text-gray-900 border-gray-200/80 hover:border-yellow-300 shadow-2xs"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="text-xl">⚡</span>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                  selectedGameKey === "tap" ? "bg-black/20 text-black" : "bg-yellow-100 text-yellow-800"
                }`}>
                  Tap
                </span>
              </div>
              <div className="mt-3">
                <div className="text-[11px] font-bold opacity-80 uppercase">Tap Game</div>
                <div className="text-lg font-black font-mono mt-0.5">
                  {(gamesSummary.tap?.points || 0).toLocaleString()}
                </div>
                <div className="text-[10px] opacity-70">
                  {(gamesSummary.tap?.sessions || 0).toLocaleString()} sessions
                </div>
              </div>
            </div>

            {/* Boom Cards */}
            <div
              onClick={() => handleGameSelect("boomcards")}
              className={`p-4 rounded-2xl border transition cursor-pointer ${
                selectedGameKey === "boomcards"
                  ? "bg-blue-600 text-white border-blue-700 shadow-md scale-102"
                  : "bg-white text-gray-900 border-gray-200/80 hover:border-blue-300 shadow-2xs"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="text-xl">🃏</span>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                  selectedGameKey === "boomcards" ? "bg-black/30 text-white" : "bg-blue-100 text-blue-800"
                }`}>
                  Boom
                </span>
              </div>
              <div className="mt-3">
                <div className="text-[11px] font-bold opacity-80 uppercase">Boom Cards</div>
                <div className="text-lg font-black font-mono mt-0.5">
                  {(gamesSummary.boomcards?.points || 0).toLocaleString()}
                </div>
                <div className="text-[10px] opacity-70">
                  {(gamesSummary.boomcards?.sessions || 0).toLocaleString()} sessions
                </div>
              </div>
            </div>

            {/* 777 Slots */}
            <div
              onClick={() => handleGameSelect("slots")}
              className={`p-4 rounded-2xl border transition cursor-pointer ${
                selectedGameKey === "slots"
                  ? "bg-purple-600 text-white border-purple-700 shadow-md scale-102"
                  : "bg-white text-gray-900 border-gray-200/80 hover:border-purple-300 shadow-2xs"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="text-xl">🎰</span>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                  selectedGameKey === "slots" ? "bg-black/30 text-white" : "bg-purple-100 text-purple-800"
                }`}>
                  Slots
                </span>
              </div>
              <div className="mt-3">
                <div className="text-[11px] font-bold opacity-80 uppercase">777 Slots</div>
                <div className="text-lg font-black font-mono mt-0.5">
                  {(gamesSummary.slots?.points || 0).toLocaleString()}
                </div>
                <div className="text-[10px] opacity-70">
                  {(gamesSummary.slots?.sessions || 0).toLocaleString()} sessions
                </div>
              </div>
            </div>

            {/* Spin Wheel */}
            <div
              onClick={() => handleGameSelect("spinwheel")}
              className={`p-4 rounded-2xl border transition cursor-pointer ${
                selectedGameKey === "spinwheel"
                  ? "bg-emerald-600 text-white border-emerald-700 shadow-md scale-102"
                  : "bg-white text-gray-900 border-gray-200/80 hover:border-emerald-300 shadow-2xs"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="text-xl">🎡</span>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                  selectedGameKey === "spinwheel" ? "bg-black/30 text-white" : "bg-emerald-100 text-emerald-800"
                }`}>
                  Spin
                </span>
              </div>
              <div className="mt-3">
                <div className="text-[11px] font-bold opacity-80 uppercase">Spin Wheel</div>
                <div className="text-lg font-black font-mono mt-0.5">
                  {(gamesSummary.spinwheel?.points || 0).toLocaleString()}
                </div>
                <div className="text-[10px] opacity-70">
                  {(gamesSummary.spinwheel?.sessions || 0).toLocaleString()} sessions
                </div>
              </div>
            </div>
          </div>

          {/* Filters & Ledger Table */}
          <div className="bg-white rounded-2xl border border-gray-200/80 shadow-2xs p-5 space-y-4">
            {/* Game Tabs & Filters */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 pb-3">
              {/* Game Key Filter Tabs */}
              <div className="flex flex-wrap items-center gap-1.5">
                {GAME_TABS.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => handleGameSelect(tab.key)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer ${
                      selectedGameKey === tab.key
                        ? "bg-black text-[#ffff00] shadow-sm"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    <span>{tab.icon}</span>
                    <span>{tab.label}</span>
                  </button>
                ))}
              </div>

              {/* User ID Direct Filter input */}
              <div className="flex items-center gap-2">
                <div className="relative">
                  <FiUser className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
                  <input
                    type="text"
                    placeholder="Filter User ID..."
                    value={userIdFilter}
                    onChange={(e) => {
                      setUserIdFilter(e.target.value);
                      setPage(1);
                    }}
                    className="pl-8 pr-3 py-1.5 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-900 focus:outline-none focus:border-yellow-400 focus:bg-white w-36 sm:w-44"
                  />
                  {userIdFilter && (
                    <button
                      onClick={() => setUserIdFilter("")}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-black cursor-pointer"
                    >
                      <FiX className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Search row */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="relative flex-1 max-w-md">
                <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search user, session ID, or event name..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-900 focus:outline-none focus:border-yellow-400 focus:bg-white"
                />
              </div>

              <div className="text-xs text-gray-500 font-medium">
                Showing <span className="font-bold text-gray-800">{history.length}</span> records on this page
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto border border-gray-100 rounded-xl">
              <table className="w-full text-left text-xs">
                <thead className="bg-gray-50 text-gray-500 font-bold uppercase tracking-wider text-[10px] border-b border-gray-200">
                  <tr>
                    <th className="py-3 px-4">Session / Date</th>
                    <th className="py-3 px-4">User</th>
                    <th className="py-3 px-4">Game</th>
                    <th className="py-3 px-4">Event</th>
                    <th className="py-3 px-4 text-right">Game Details</th>
                    <th className="py-3 px-4 text-right">Points Credited</th>
                    <th className="py-3 px-4 text-center">Status</th>
                    <th className="py-3 px-4 text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loading ? (
                    <tr>
                      <td colSpan={8} className="py-12 text-center text-gray-400">
                        <FiRefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-yellow-500" />
                        <span>Loading Event Points Ledger...</span>
                      </td>
                    </tr>
                  ) : history.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="py-12 text-center text-gray-400">
                        No event points activity found matching criteria.
                      </td>
                    </tr>
                  ) : (
                    history.map((row, idx) => {
                      const sid = row.sessionId || row.id || row.gameSessionId || `tx_${idx}`;
                      const uid = row.userId || row.user_id || row.user?.id || "—";
                      const uname =
                        row.user?.username ||
                        row.username ||
                        row.user?.name ||
                        `User #${uid}`;
                      const gKey = row.gameKey || row.gameType || row.game || selectedGameKey;
                      const badge = getGameBadge(gKey);
                      const pts = Number(
                        row.eventPoints ?? row.pointsAwarded ?? row.points ?? row.claimedPoints ?? row.amount ?? 0
                      );
                      const score = row.score ?? row.gameScore;
                      const statusStr = String(row.status || "completed").toLowerCase();

                      return (
                        <tr key={sid} className="hover:bg-gray-50 transition">
                          <td className="py-3 px-4 whitespace-nowrap">
                            <div className="flex items-center gap-1.5 font-mono text-gray-900 font-bold">
                              <span>{String(sid).slice(0, 14)}...</span>
                              <button
                                onClick={() => copyToClipboard(sid, sid)}
                                className="text-gray-400 hover:text-gray-800 p-0.5"
                                title="Copy ID"
                              >
                                {copiedId === sid ? (
                                  <FiCheck className="w-3 h-3 text-emerald-600" />
                                ) : (
                                  <FiCopy className="w-3 h-3" />
                                )}
                              </button>
                            </div>
                            <div className="text-[10px] text-gray-400 mt-0.5">
                              {formatDateTime(row.createdAt || row.completedAt || row.transactionDate || row.timestamp)}
                            </div>
                          </td>

                          <td className="py-3 px-4">
                            <Link
                              to={`/user/${uid}/coin-history`}
                              className="font-bold text-gray-900 hover:text-yellow-600 transition flex items-center gap-1.5"
                            >
                              <span>{uname}</span>
                              <FiExternalLink className="w-3 h-3 text-gray-400" />
                            </Link>
                            <div className="text-[10px] text-gray-400">ID: #{uid}</div>
                          </td>

                          <td className="py-3 px-4 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold border ${badge.style}`}
                            >
                              <span>{badge.icon}</span>
                              <span>{badge.label}</span>
                            </span>
                          </td>

                          <td className="py-3 px-4 text-gray-600">
                            <span className="font-medium text-gray-800">
                              {row.eventTitle || row.eventName || (row.eventId ? `Event #${row.eventId}` : "Zeta Rush")}
                            </span>
                          </td>

                          <td className="py-3 px-4 text-right font-mono text-gray-700">
                            {score != null ? (
                              <span className="font-bold text-gray-900">Score: {Number(score).toLocaleString()}</span>
                            ) : (
                              row.details || "—"
                            )}
                          </td>

                          <td className="py-3 px-4 text-right">
                            <span className="font-mono font-black text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-200/60">
                              +{pts.toLocaleString()} pts
                            </span>
                          </td>

                          <td className="py-3 px-4 text-center">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                                statusStr === "completed" || statusStr === "claimed" || statusStr === "success"
                                  ? "bg-emerald-100 text-emerald-800"
                                  : statusStr === "disqualified"
                                  ? "bg-rose-100 text-rose-800"
                                  : "bg-blue-100 text-blue-800"
                              }`}
                            >
                              {statusStr}
                            </span>
                          </td>

                          <td className="py-3 px-4 text-center">
                            <button
                              onClick={() => setSelectedSession(row)}
                              className="p-1.5 rounded-lg bg-gray-100 hover:bg-black hover:text-white text-gray-700 transition cursor-pointer"
                              title="View Details"
                            >
                              <FiEye className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2 text-xs text-gray-500">
              <div className="flex items-center gap-2">
                <span>Show</span>
                <select
                  value={perPage}
                  onChange={(e) => {
                    setPerPage(Number(e.target.value));
                    setPage(1);
                  }}
                  className="bg-white border border-gray-200 rounded-lg px-2 py-1 text-xs"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span>records per page (Total {pagination.total.toLocaleString()})</span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1 || loading}
                  className="px-3 py-1 bg-white border border-gray-200 rounded-lg font-bold text-gray-700 hover:bg-gray-100 disabled:opacity-40 cursor-pointer"
                >
                  Prev
                </button>
                <span className="font-bold text-gray-800">
                  Page {page} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                  disabled={page >= pagination.totalPages || loading}
                  className="px-3 py-1 bg-white border border-gray-200 rounded-lg font-bold text-gray-700 hover:bg-gray-100 disabled:opacity-40 cursor-pointer"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {selectedSession && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl space-y-4 border border-gray-200">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <div>
                <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                  <span>🎯 Event Points Session</span>
                </h3>
                <p className="text-xs text-gray-400 font-mono mt-0.5">
                  ID: #{selectedSession.sessionId || selectedSession.id || "—"}
                </p>
              </div>
              <button
                onClick={() => setSelectedSession(null)}
                className="p-1 text-gray-400 hover:text-black rounded-lg cursor-pointer"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3 text-xs">
              <div className="bg-gray-50 rounded-xl p-3 grid grid-cols-2 gap-3">
                <div>
                  <span className="text-gray-500 block">User:</span>
                  <span className="font-bold text-gray-900">
                    {selectedSession.user?.username || selectedSession.username || `User #${selectedSession.userId}`}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 block">User ID:</span>
                  <span className="font-bold text-gray-900">#{selectedSession.userId}</span>
                </div>
                <div>
                  <span className="text-gray-500 block">Game:</span>
                  <span className="font-bold text-gray-900">
                    {selectedSession.gameKey || selectedSession.gameType || "Mini Game"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 block">Event Points Credited:</span>
                  <span className="font-mono font-black text-base text-amber-600">
                    +{Number(selectedSession.eventPoints || selectedSession.points || selectedSession.amount || 0).toLocaleString()} pts
                  </span>
                </div>
                {selectedSession.score != null && (
                  <div>
                    <span className="text-gray-500 block">Score Achieved:</span>
                    <span className="font-mono font-bold text-gray-800">
                      {Number(selectedSession.score).toLocaleString()}
                    </span>
                  </div>
                )}
                <div>
                  <span className="text-gray-500 block">Status:</span>
                  <span className="font-bold uppercase text-emerald-700">
                    {selectedSession.status || "completed"}
                  </span>
                </div>
              </div>

              <div className="bg-amber-50/70 border border-amber-200/60 rounded-xl p-3 text-[11px] text-amber-900 space-y-1">
                <div className="font-bold">Event Association:</div>
                <div>{selectedSession.eventTitle || selectedSession.eventName || "Active Event"}</div>
                <div className="text-gray-500 mt-1">
                  Recorded At: {formatDateTime(selectedSession.createdAt || selectedSession.completedAt || selectedSession.transactionDate)}
                </div>
              </div>
            </div>

            <div className="pt-2 flex justify-end gap-2 border-t border-gray-100">
              <Link
                to={`/user/${selectedSession.userId}/coin-history`}
                className="px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold text-xs flex items-center gap-1.5 transition"
              >
                <span>View User Coin History</span>
                <FiExternalLink className="w-3 h-3" />
              </Link>
              <button
                onClick={() => setSelectedSession(null)}
                className="px-4 py-2 rounded-xl bg-black text-[#ffff00] font-bold text-xs hover:bg-gray-800 transition cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
